import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@george-labs.com/design-system"
import { ICON } from "@george-labs.com/george-assets/icons"
import {
  LoanCaseStatusDTO,
  LoanCaseStatusDTOStatusEnum,
} from "@smartx/mikro-vt-client-api"
import useValueListData from "./valuelist"
import { ClosedCaseTypeList, SuccessCaseTypeList } from "../utils/constants"
import { useCaseEventsByCaseId } from "../app/hooks"
import { VALUE_LIST_ENUM } from "../functions/setCodeTableValues"
import { getPalAnnualRevisionKoNotifications } from "../functions/caseEventsHelper"

type BadgeVariant = (typeof Badge.VARIANT)[keyof typeof Badge.VARIANT]

export interface StatusBadgeData {
  variant: BadgeVariant
  icon: ICON
  text: string
}

const PAL_ANNUAL_REVISION = LoanCaseStatusDTOStatusEnum.PalAnnualRevision

export const useStatusBadge = (
  loanCaseStatus?: LoanCaseStatusDTO,
  caseId?: string,
): StatusBadgeData => {
  const { t } = useTranslation()
  const status: string = loanCaseStatus?.status ?? ""
  const cvtText = useValueListData(VALUE_LIST_ENUM.OnlineStateList, status)
  const isRectification = !!loanCaseStatus?.onlineInfo?.onlineRectification
  const isSuspended = !!loanCaseStatus?.suspended
  const caseEvents = useCaseEventsByCaseId(caseId ?? "")

  return useMemo<StatusBadgeData>(() => {
    if (isRectification) {
      return {
        variant: Badge.VARIANT.WARNING,
        icon: ICON.WARNING,
        text: t("rectification.statusName"),
      }
    }

    let variant: BadgeVariant = Badge.VARIANT.STATE
    let icon: ICON = ICON.CLOCK
    let text = isSuspended
      ? `${cvtText}${t("case.status.postfix.SUSPENDED")}`
      : cvtText

    if (ClosedCaseTypeList.includes(status)) {
      variant = Badge.VARIANT.ERROR
      icon = ICON.ERROR_CIRCLE
    } else if (SuccessCaseTypeList.includes(status)) {
      variant = Badge.VARIANT.SUCCESS
      icon = ICON.CHECKMARK_CIRCLE

      if (status === PAL_ANNUAL_REVISION) {
        const hasKoNotifications = !!getPalAnnualRevisionKoNotifications(
          status,
          caseId,
          caseEvents,
        )
        if (hasKoNotifications) {
          variant = Badge.VARIANT.WARNING
          icon = ICON.WARNING
          text = t("case.status.PAL_REVIEW_TODO")
        } else {
          variant = Badge.VARIANT.POOL
          icon = ICON.CLOCK
        }
      }
    }

    return { variant, icon, text }
  }, [status, isRectification, isSuspended, caseEvents, caseId, cvtText, t])
}
